import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { isTransientNetworkError } from "@krea/shared/utils/isTransientNetworkError";

import { useAuthStore } from "../../auth/authStore";
import type { BankIDAuthConfig, BankIDError } from "../common-types";
import {
  authenticateViaBankID,
  cancelViaBankID,
  collectViaBankID,
} from "./api";
import {
  type ISEBankIDCollectResponse,
  SEBankIDAuthType,
  SEBankIDFlowState,
  SEBankIDHintCode,
  type SEBankIDState,
} from "./types";

const BANKID_COLLECT_QUERY_KEY = "se-bankid-collect";
const POLL_INTERVAL = 2000;

/**
 * How many collect polls in a row may fail at the transport level before we give
 * up on the authentication.
 *
 * Switching to the BankID app suspends the page on iOS and kills the in-flight
 * request, which costs one or two polls — but the order is still valid and the
 * user is signing. At the 2s cadence this is ~10s of silence, long enough to
 * cover the hand-off and a brief loss of signal, short enough that a genuinely
 * broken connection still surfaces.
 */
const MAX_CONSECUTIVE_TRANSIENT_COLLECT_FAILURES = 5;

const MAX_TRANSIENT_AUTHENTICATE_RETRIES = 2;

interface UseSEBankIDConfig extends BankIDAuthConfig {
  /**
   * SSN for testing purposes (SE only, non-production)
   */
  testSSN?: string;
  extraData?: {
    preferredLanguage?: string | null;
    gaClientId?: string | null;
  };
}

export const useSEBankID = (config: UseSEBankIDConfig) => {
  const {
    authenticationContext,
    identificationContext,
    onSuccess,
    onError,
    onCancel,
    testSSN,
    extraData,
  } = config;

  const { t } = useTranslation();
  const { setAccessToken, setRefreshToken } = useAuthStore();
  const queryClient = useQueryClient();
  const [authType, setAuthType] = useState<SEBankIDAuthType | null>(null);
  const [state, setState] = useState<SEBankIDState>({ status: "idle" });
  const [qrCode, setQrCode] = useState<string | null>(null);
  // Track if the component is mounted to prevent double starts
  const isMountedRef = useRef(true);

  // Track if authentication is active to prevent double starts
  const isAuthenticatingRef = useRef(false);

  // Collect polls that died in transport since the last one that got an answer
  const transientCollectFailuresRef = useRef(0);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      queryClient.cancelQueries({
        queryKey: [BANKID_COLLECT_QUERY_KEY],
        exact: false,
      });
    };
  }, [queryClient]);

  // Authenticate mutation
  const authenticateMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticateViaBankID(testSSN);

      return response.data;
    },
    retry: (failureCount, err) =>
      isTransientNetworkError(err) &&
      failureCount < MAX_TRANSIENT_AUTHENTICATE_RETRIES,
    retryDelay: 1000,
    onSuccess: (data) => {
      if (!isMountedRef.current) return;

      if (data.qrCode) setQrCode(data.qrCode);

      setState({
        status: "pending",
        authType: authType ?? undefined,
        hintCode: SEBankIDHintCode.STARTED,
      });
      isAuthenticatingRef.current = false;
    },
    onError: (err) => {
      if (!isMountedRef.current) return;

      console.error("Failed to start BankID authentication", err);
      Sentry.captureException(err);

      const error: BankIDError = {
        code: "AUTH_START_FAILED",
        message: "Failed to start authentication",
      };
      setState({
        status: "error",
        error,
        hintCode: SEBankIDHintCode.START_FAILED,
      });
      isAuthenticatingRef.current = false;

      if (onError) onError(error);
    },
  });

  const resetAuthentication = useCallback(() => {
    authenticateMutation.reset();
    setQrCode(null);
  }, [authenticateMutation]);

  const resetBankIdApplication = useCallback(() => {
    queryClient.cancelQueries({ queryKey: [BANKID_COLLECT_QUERY_KEY] });
    queryClient.resetQueries({
      queryKey: [BANKID_COLLECT_QUERY_KEY],
      exact: false,
    });
    resetAuthentication();
    isAuthenticatingRef.current = false;
    transientCollectFailuresRef.current = 0;
  }, [queryClient, resetAuthentication]);

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (authenticateMutation.data?.orderRef) {
        await cancelViaBankID(authenticateMutation.data.orderRef);
      }
    },
    onMutate: () => {
      resetBankIdApplication();
    },
  });

  // Collect query - polls for authentication status
  const collectQuery = useQuery<ISEBankIDCollectResponse, AxiosError>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [BANKID_COLLECT_QUERY_KEY, authenticateMutation.data?.orderRef],
    queryFn: async () => {
      const orderRef = authenticateMutation.data?.orderRef;
      if (!orderRef) throw new Error("No orderRef available");

      const response = await collectViaBankID({
        orderRef,
        extraData: extraData ?? null,
        identificationContext: identificationContext ?? null,
        authenticationContext,
      });

      return response.data;
    },
    enabled:
      !!authenticateMutation.data?.orderRef && state.status === "pending",
    refetchInterval: (query) => {
      // Stop polling if we have a terminal state
      if (
        query.state.data?.status === SEBankIDFlowState.COMPLETE ||
        query.state.data?.status === SEBankIDFlowState.FAILED
      ) {
        return false;
      }

      if (import.meta.env.MODE === "test") {
        return 200;
      }

      return POLL_INTERVAL;
    },
    refetchIntervalInBackground: true,
    retry: false,
    staleTime: 0,
  });

  // Handle collect query results
  useEffect(() => {
    if (!collectQuery.data || !isMountedRef.current) return;

    const { status, hintCode, qrCode: newQrCode, tokens } = collectQuery.data;

    // Update QR code if it changed
    if (newQrCode && newQrCode !== qrCode) {
      setQrCode(newQrCode);
    }

    if (
      status === SEBankIDFlowState.PENDING &&
      hintCode &&
      !cancelMutation.isPending
    ) {
      setState((prev) => {
        if ("hintCode" in prev && prev.hintCode === hintCode) return prev;

        return {
          status: "pending",
          hintCode,
        };
      });
    }

    if (status === SEBankIDFlowState.COMPLETE) {
      // Authentication successful
      setState({ status: "success" });
      resetAuthentication();

      setAccessToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);

      onSuccess({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      });
    } else if (status === SEBankIDFlowState.FAILED) {
      // Authentication failed
      const isUserCancellation = hintCode === SEBankIDHintCode.USER_CANCEL;

      if (isUserCancellation) {
        setState({ status: "cancelled", hintCode });
      } else if (hintCode === SEBankIDHintCode.START_FAILED) {
        // Restart authentication on START_FAILED
        console.warn("BankID start failed, restarting...");
        queryClient.setQueryData(
          [BANKID_COLLECT_QUERY_KEY, authenticateMutation.data?.orderRef],
          {
            status: SEBankIDFlowState.PENDING,
            hintCode: SEBankIDHintCode.OUTSTANDING_TRANSACTION,
          },
        );

        // Trigger a new authenticate call
        if (!isAuthenticatingRef.current && authType) {
          isAuthenticatingRef.current = true;
          authenticateMutation.mutate();
        }

        return;
      } else {
        const error: BankIDError = {
          code: hintCode ?? "UNKNOWN_ERROR",
          message: "Authentication failed",
          isUserCancellation,
        };
        console.error(
          "Failed to collect BankID authentication",
          collectQuery.data,
        );

        Sentry.captureMessage(
          `SE BankID error code: ${JSON.stringify(collectQuery.data)}`,
        );
        setState({
          status: "error",
          error,
          hintCode: hintCode ?? SEBankIDHintCode.ERROR,
        });

        if (onError) onError(error);
      }

      resetAuthentication();
    }
  }, [
    collectQuery.data,
    qrCode,
    resetAuthentication,
    onSuccess,
    onError,
    queryClient,
    authenticateMutation.data?.orderRef,
    authType,
    authenticateMutation,
    cancelMutation.isPending,
    setAccessToken,
    setRefreshToken,
  ]);

  // A successful collect clears the transient-failure budget. Keyed on
  // dataUpdatedAt rather than data because React Query's structural sharing keeps
  // the same object when a poll returns a payload identical to the previous one.
  useEffect(() => {
    if (collectQuery.dataUpdatedAt) transientCollectFailuresRef.current = 0;
  }, [collectQuery.dataUpdatedAt]);

  // Handle collect query errors
  useEffect(() => {
    if (!collectQuery.error || !isMountedRef.current) return;

    const isTransient = isTransientNetworkError(collectQuery.error);

    if (
      isTransient &&
      transientCollectFailuresRef.current <
        MAX_CONSECUTIVE_TRANSIENT_COLLECT_FAILURES
    ) {
      transientCollectFailuresRef.current += 1;

      // Deliberately leaves the state alone. `enabled` only holds while the status
      // is "pending", so failing here would clear the orderRef and stop the poll
      // for good — which is exactly how an app switch used to kill a signing that
      // was still valid on BankID's side. Left untouched, the existing
      // refetchInterval simply polls again.
      Sentry.addBreadcrumb({
        category: "bankid",
        level: "info",
        message: "SE BankID collect never reached the backend, still polling",
        data: {
          consecutiveFailures: transientCollectFailuresRef.current,
          code: collectQuery.error.code,
        },
      });

      return;
    }

    const httpStatus =
      collectQuery.error.response?.status ?? collectQuery.error.status;
    let error: BankIDError;

    if (httpStatus === 403) {
      error = {
        code: collectQuery.error.code ?? "UNAUTHORIZED",
        message: t("bankID.errors.unauthorized"),
      };
    } else if (isTransient) {
      console.error(
        "Collecting BankID kept failing in transport:",
        collectQuery.error,
      );
      Sentry.captureException(collectQuery.error, {
        tags: { bankIdFailure: "collect-transport" },
      });

      error = {
        code: "NETWORK_ERROR",
        message: t("bankID.errors.networkError"),
      };
    } else {
      console.error("Collecting BankID failed:", collectQuery.error);
      Sentry.captureException(collectQuery.error, {
        tags: { bankIdFailure: "collect" },
      });

      error = {
        code: "COLLECT_FAILED",
        message: t("bankID.errors.genericError"),
      };
    }

    setState({ status: "error", error, hintCode: SEBankIDHintCode.ERROR });
    resetAuthentication();

    if (onError) onError(error);
  }, [collectQuery.error, resetAuthentication, onError, t]);

  // Coming back from the BankID app should be felt immediately rather than after
  // the next interval tick. React Query's focus handling covers the common case,
  // but a custom-scheme navigation can park the page in the back/forward cache,
  // where only pageshow fires.
  const orderRef = authenticateMutation.data?.orderRef;

  useEffect(() => {
    if (state.status !== "pending" || !orderRef) return;

    const collectNow = () => {
      if (document.visibilityState !== "visible") return;

      queryClient.refetchQueries(
        { queryKey: [BANKID_COLLECT_QUERY_KEY, orderRef] },
        // refetchQueries defaults to cancelRefetch: true, which drops the
        // in-flight poll and issues a second request. BankID hands out the tokens
        // for an order exactly once, so that duplicate answers `failed` for an
        // order already consumed by the request we just walked away from — and it
        // is the duplicate's answer that React Query keeps. Join the poll already
        // in flight instead; only start one when nothing is running.
        { cancelRefetch: false },
      );
    };

    document.addEventListener("visibilitychange", collectNow);
    window.addEventListener("pageshow", collectNow);

    return () => {
      document.removeEventListener("visibilitychange", collectNow);
      window.removeEventListener("pageshow", collectNow);
    };
  }, [state.status, orderRef, queryClient]);

  // Start authentication
  const start = useCallback(
    (newAuthType?: SEBankIDAuthType) => {
      if (isAuthenticatingRef.current) {
        console.warn("Authentication already in progress");

        return;
      }

      const typeToUse = newAuthType ?? SEBankIDAuthType.QR_CODE;
      setAuthType(typeToUse);
      setState({ status: "authenticating", authType: typeToUse });
      isAuthenticatingRef.current = true;
      transientCollectFailuresRef.current = 0;
      authenticateMutation.mutate();
    },
    [authenticateMutation],
  );

  // Reset to initial state
  const reset = useCallback(() => {
    resetBankIdApplication();
    setAuthType(null);
    setState({ status: "idle" });
  }, [resetBankIdApplication]);

  // Cancel authentication
  const cancel = useCallback(() => {
    cancelMutation.mutate(undefined, {
      onSuccess: () => {
        if (onCancel) onCancel();
        setAuthType(null);
        setState({ status: "idle" });
      },
    });
  }, [cancelMutation, onCancel]);

  // Change auth type (switch between QR and auto-start)
  const changeAuthType = useCallback(
    (newAuthType: SEBankIDAuthType) => {
      if (state.status !== "pending") {
        console.warn("Can only change auth type during pending state");

        return;
      }

      // Cancel current authentication
      if (authenticateMutation.data?.orderRef) {
        cancelMutation.mutate();
      }

      // Start with new auth type
      setAuthType(newAuthType);
      setState({ status: "authenticating", authType: newAuthType });
      isAuthenticatingRef.current = true;
      authenticateMutation.mutate();
    },
    [state.status, authenticateMutation, cancelMutation],
  );

  return {
    state,
    start,
    cancel,
    reset,
    changeAuthType,
    qrCode,
    autoStartToken: authenticateMutation.data?.autoStartToken,
    authType,
  };
};
